import { retryAfterMs } from "@baton/api";
import type { FocusSignal, KeyValueStore, OnlineSignal, Platform, RefusalCause, ShellReport, StreamSource, UploadSender } from "@baton/core";
import { memoryStore, readSseStream } from "@baton/core";

/**
 * The browser implementations of `@baton/core`'s platform seams: `fetch` over the event stream for the
 * stream, `navigator.onLine` for the online signal, the window focus event, `localStorage`
 * behind a try/catch fallback, `XMLHttpRequest` for upload progress, `crypto.subtle` for
 * the sha256, and the Web Locks API with `BroadcastChannel` for the tabs' shared stream, loaded from
 * `tabs.ts` as a chunk of its own when the stream engine first asks for it. This is the one directory outside `@baton/api` and `@baton/core` that names
 * a network API, and the lint rule's allowlist says so.
 */
/** Which 429 refused the stream, read from the problem body, which this consumes whatever it holds. */
async function refusalCause(response: Response): Promise<RefusalCause> {
  try {
    const body = (await response.json()) as { reason?: unknown };
    return body.reason === "stream_limit" ? "stream_limit" : "rate_limit";
  } catch {
    return "rate_limit";
  }
}

/** The body of an answer that is not the stream, as JSON, or null when it is not JSON (019's 202 `queued` and its problems are). */
async function jsonBodyOf(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

const streamSource: StreamSource = {
  open(url, lastEventId, handlers, request) {
    // `fetch` rather than `EventSource`, so the reconstructed connection can carry
    // `Last-Event-ID` and the keepalive comments reach the dead-connection timer
    // (`@baton/core` events/stream/sse.ts says why), and so a stream a `POST` opens (019's turn start) is the same connection.
    const controller = new AbortController();
    const headers: Record<string, string> = { Accept: "text/event-stream", "Cache-Control": "no-store", ...request?.headers };
    if (lastEventId !== null) headers["Last-Event-ID"] = lastEventId;
    void (async () => {
      let response: Response;
      try {
        response = await fetch(url, { headers, credentials: "same-origin", signal: controller.signal, ...(request === undefined ? {} : { method: request.method, body: request.body }) });
      } catch {
        if (!controller.signal.aborted) handlers.onError();
        return;
      }
      if (response.status === 429) {
        // An answer, not an outage. The problem names 001 FR-149's stream bound with `reason`; any other 429 is a request budget.
        const wait = retryAfterMs(response.headers);
        const cause = await refusalCause(response);
        if (!controller.signal.aborted) handlers.onRefused(wait, cause);
        return;
      }
      // A `POST` that answered without a stream: the queued 202, or a problem, which the request's own handler reads.
      if (request !== undefined && !(response.headers.get("Content-Type") ?? "").startsWith("text/event-stream")) {
        const body = await jsonBodyOf(response);
        if (!controller.signal.aborted) request.onResponse(response.status, body);
        return;
      }
      if (!response.ok || response.body === null) {
        void response.body?.cancel().catch(() => undefined);
        if ((response.status === 404 || response.status === 501) && handlers.onUnavailable !== undefined) handlers.onUnavailable(response.status);
        else handlers.onError();
        return;
      }
      handlers.onOpen();
      try {
        await readSseStream(response.body, { onMessage: handlers.onMessage, onComment: handlers.onKeepalive }, controller.signal);
      } catch {
        if (!controller.signal.aborted) handlers.onError();
        return;
      }
      // The server closed the stream without an `event: closed`: a proxy or a restart.
      if (!controller.signal.aborted) handlers.onError();
    })();
    return {
      close() {
        controller.abort();
      },
    };
  },
};

const onlineSignal: OnlineSignal = {
  get online() {
    return navigator.onLine;
  },
  subscribe(listener) {
    const on = () => {
      listener(true);
    };
    const off = () => {
      listener(false);
    };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  },
};

const focusSignal: FocusSignal = {
  subscribe(listener) {
    const onVisible = () => {
      if (document.visibilityState === "visible") listener();
    };
    window.addEventListener("focus", listener);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", listener);
      document.removeEventListener("visibilitychange", onVisible);
    };
  },
};

function localStore(): KeyValueStore {
  try {
    const probe = "__baton_probe__";
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
  } catch {
    return memoryStore();
  }
  return {
    get: (key) => localStorage.getItem(key),
    set: (key, value) => {
      try {
        localStorage.setItem(key, value);
      } catch {
        // Quota exhausted or storage disabled mid-session: a convenience is lost, nothing throws.
      }
    },
    remove: (key) => {
      localStorage.removeItem(key);
    },
    keys: () => Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i) ?? "").filter((k) => k.length > 0),
  };
}

/**
 * A same-origin-in-production backend URL (an upload grant, a signed attachment download) is
 * served behind the page's own reverse proxy, so routing it through `pathname + search` lets the
 * browser resolve it against the current origin instead of the absolute `instance.base_url` it
 * was built from — a no-op where the two already match, and what keeps a split dev topology (the
 * frontend dev server proxying `/api` to a backend on another port) from turning a same-origin
 * request into a cross-origin one CORS was never configured for. `@baton/core` has no DOM
 * dependency (this file's own doc comment), so its signed-download reader takes an already-resolved
 * URL from its caller instead of doing this itself — `item/panels/attachments/attachmentActions.ts`
 * is that caller for the in-app readers.
 */
export function pageRelativeUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.href);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}

const xhrSender: UploadSender = {
  send(request) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(request.method, pageRelativeUrl(request.url));
      for (const [name, value] of Object.entries(request.headers)) xhr.setRequestHeader(name, value);
      xhr.upload.onprogress = (event) => {
        request.onProgress({ loaded: event.loaded, total: event.lengthComputable ? event.total : request.body.size });
      };
      xhr.onload = () => {
        const headers: Record<string, string> = {};
        for (const line of xhr.getAllResponseHeaders().trim().split(/[\r\n]+/)) {
          const [name, ...rest] = line.split(": ");
          if (name) headers[name.toLowerCase()] = rest.join(": ");
        }
        resolve({ status: xhr.status, headers });
      };
      xhr.onerror = () => {
        reject(new Error("upload failed"));
      };
      xhr.onabort = () => {
        reject(new DOMException("upload cancelled", "AbortError"));
      };
      request.signal.addEventListener("abort", () => {
        xhr.abort();
      });
      xhr.send(request.body);
    });
  },
};

async function hashSha256(blob: Blob): Promise<string | null> {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

/** The browser build has no desktop shell; feature 017's adapter replaces this report. */
const shell: ShellReport = { hasShell: false, shellUploader: null };

export function browserPlatform(): Platform {
  return { stream: streamSource, online: onlineSignal, focus: focusSignal, storage: localStore(), upload: xhrSender, shell, hashSha256, loadTabs: () => import("./tabs.ts").then(({ browserTabs }) => browserTabs()) };
}
